import React, { useState, useContext, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { StateContext } from '../StateProvider';
import './Navigation.css';

import logo from '../../assets/Navigation/logo_long.png';
import logoSmall from '../../assets/Navigation/logoSmall.png';
import menuButton from '../../assets/Navigation/menuButton.png';
import closeButton from '../../assets/Navigation/closeButton.png';

const Collapsible = ({ open, children }) => {
  const ref = useRef(null);
  const [maxHeight, setMaxHeight] = useState('0px');

  useEffect(() => {
    if (!ref.current) return;
    const h = ref.current.scrollHeight;
    setMaxHeight(open ? `${h}px` : '0px');
  }, [open, children]);

  return (
    <div style={{ overflow: 'hidden', maxHeight, transition: 'max-height 300ms ease' }}>
      <div ref={ref}>{children}</div>
    </div>
  );
};

const Navigation = () => {
  const { role, loggedIn } = useContext(StateContext);
  const navigate = useNavigate();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [openSections, setOpenSections] = useState({
    services: false,
    products: false,
    company: false,
  });

  const toggleDropdown = () => setIsDropdownOpen(prev => !prev);
  const closeDropdown = () => {
    setIsDropdownOpen(false);
    setOpenSections({ services: false, products: false, company: false });
  };

  const loginClicked = () => {
    navigate('/login');
    window.scrollTo(0, 0);
  };

  const adminClicked = () => {
    navigate('/console');
    window.scrollTo(0, 0);
  };

  const toggleSection = (key) =>
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <nav className="navbar">
      <div className="container">
        <Link to="/" className="navbar-brand">
          <img className="navLogo" src={logo} alt="Logo" />
          <img className="navLogoSmall" src={logoSmall} alt="Small Logo" />
        </Link>
        <div className="navbar-buttons">
          {!loggedIn && (
            <button className="navbar-login-button" onClick={loginClicked}>
              LOGIN
            </button>
          )}
          {(loggedIn && (role === 'admin' || role === 'staff')) && (
            <button className="navbar-login-button" onClick={adminClicked}>
              Team
            </button>
          )}
          <button
            className={`navbar-toggle ${isDropdownOpen ? 'open' : ''}`}
            onClick={toggleDropdown}
          >
            <img className="dropdown-icon" src={menuButton} alt="Dropdown Icon" />
          </button>
        </div>
      </div>

      {isDropdownOpen && (
        <ul className="dropdown-menu">
          <div className="dropdown-top">
            <div className="dropdown-left">
              <Link to="/" className="navbar-brand" onClick={closeDropdown}>
                <img className="navLogoSmall" src={logoSmall} alt="Small Logo" />
              </Link>
            </div>
            <div className="dropdown-right">
              <button className="close-button" onClick={closeDropdown}>
                <img src={closeButton} alt="Close Button" className='close-button-image' />
              </button>
            </div>
          </div>

          <div className="dropdown-items">
            {/* PRICES — top-level category */}
            <div className="dropdown-category">
              <h3 className='dropdown-title'>
                <Link
                  to="/service-prices"
                  className="dropdown-title"
                  onClick={closeDropdown}
                  style={{ display: 'inline-block', textDecoration: 'none', margin: '5px' }}
                >
                  Prices
                </Link>
              </h3>
              {/* If you ever want sublinks under Prices, add <li className="dropdown-item"> items here */}
            </div>

            {/* SERVICES */}
            <div className="dropdown-category">
              <h3
                className='dropdown-title'
                onClick={() => toggleSection('services')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Services {openSections.services ? '▾' : '▸'}
              </h3>
              <Collapsible open={openSections.services}>
                <li className="dropdown-item">
                  <Link to="/store/services/hotel" className="dropdown-link" onClick={closeDropdown}>
                    Hotel
                  </Link>
                </li>
                <li className="dropdown-item">
                  <Link to="/store/services/shuttle" className="dropdown-link" onClick={closeDropdown}>
                    Shuttle
                  </Link>
                </li>
                <li className="dropdown-item">
                  <Link to="/store/services/daycare" className="dropdown-link" onClick={closeDropdown}>
                    Daycare
                  </Link>
                </li>
                <li className="dropdown-item">
                  <Link to="/store/services/training" className="dropdown-link" onClick={closeDropdown}>
                    Training
                  </Link>
                </li>
                <li className="dropdown-item">
                  <Link to="/store/services/grooming" className="dropdown-link" onClick={closeDropdown}>
                    Grooming
                  </Link>
                </li>
                <li className="dropdown-item">
                  <Link to="/store/services/adventure" className="dropdown-link" onClick={closeDropdown}>
                    Adventures
                  </Link>
                </li>
              </Collapsible>
            </div>

            {/* PRODUCTS */}
            <div className="dropdown-category">
              <h3
                className='dropdown-title'
                onClick={() => toggleSection('products')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Products {openSections.products ? '▾' : '▸'}
              </h3>
              <Collapsible open={openSections.products}>
                <li className="dropdown-item">
                  <Link to="/coming-soon" className="dropdown-link" onClick={closeDropdown}>
                    Food
                  </Link>
                </li>
                <li className="dropdown-item">
                  <Link to="/coming-soon" className="dropdown-link" onClick={closeDropdown}>
                    Homes
                  </Link>
                </li>
                <li className="dropdown-item">
                  <Link to="/coming-soon" className="dropdown-link" onClick={closeDropdown}>
                    Apparels
                  </Link>
                </li>
                <li className="dropdown-item">
                  <Link to="/coming-soon" className="dropdown-link" onClick={closeDropdown}>
                    Health &amp; Wellness
                  </Link>
                </li>
              </Collapsible>
            </div>

            {/* COMPANY */}
            <div className="dropdown-category">
              <h3
                className='dropdown-title'
                onClick={() => toggleSection('company')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Company {openSections.company ? '▾' : '▸'}
              </h3>
              <Collapsible open={openSections.company}>
                <li className="dropdown-item">
                  <Link to="/about" className="dropdown-link" onClick={closeDropdown}>
                    About
                  </Link>
                </li>
                <li className="dropdown-item">
                  <Link to="/career" className="dropdown-link" onClick={closeDropdown}>
                    Career
                  </Link>
                </li>
                <li className="dropdown-item">
                  <Link to="/locations" className="dropdown-link" onClick={closeDropdown}>
                    Locations
                  </Link>
                </li>
              </Collapsible>
            </div>
          </div>
        </ul>
      )}
    </nav>
  );
};

export default Navigation;
